import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Robot, Trash, Play, Pause, ArrowsClockwise, CircleNotch, Globe, Copy } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    listarBots, criarBot, iniciarBot, pararBot, removerBot, clonarBot,
} from "../lib/api";
import DialogConfirmacao from "../componentes/DialogConfirmacao";
import BotaoSair from "../componentes/BotaoSair";


const corStatus = {
    rodando: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    parado: "text-zinc-400 bg-zinc-500/10 border-zinc-500/30",
    pausado: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    erro: "text-red-400 bg-red-500/10 border-red-500/30",
    iniciando: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
};

const labelStatus = {
    rodando: "Rodando",
    parado: "Parado",
    pausado: "Pausado",
    erro: "Erro",
    iniciando: "Iniciando",
};

export default function PaginaInicial() {
    const [bots, setBots] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [dialogAberto, setDialogAberto] = useState(false);
    const [formCriacao, setFormCriacao] = useState({ nome: "", url_alvo: "", intervalo_refresh_ms: 30000 });
    const [criando, setCriando] = useState(false);
    const navegar = useNavigate();

    const carregar = async () => {
        try {
            const lista = await listarBots();
            setBots(lista);
        } catch (e) {
            toast.error("Falha ao carregar bots");
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        carregar();
        const i = setInterval(carregar, 5000);
        return () => clearInterval(i);
    }, []);

    const submeterCriacao = async (e) => {
        e.preventDefault();
        if (!formCriacao.nome.trim() || !formCriacao.url_alvo.trim()) {
            toast.error("Informe nome e URL alvo");
            return;
        }
        setCriando(true);
        try {
            const novo = await criarBot({
                nome: formCriacao.nome.trim(),
                url_alvo: formCriacao.url_alvo.trim(),
                intervalo_refresh_ms: Number(formCriacao.intervalo_refresh_ms) || 30000,
            });
            toast.success("Bot criado");
            setDialogAberto(false);
            setFormCriacao({ nome: "", url_alvo: "", intervalo_refresh_ms: 30000 });
            await carregar();
            navegar(`/bots/${novo.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha ao criar bot");
        } finally {
            setCriando(false);
        }
    };

    const acaoBot = async (id, fn, sucesso) => {
        try {
            await fn(id);
            toast.success(sucesso);
            carregar();
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha na ação");
        }
    };

    // Confirmação de exclusão via dialog (não window.confirm).
    // O bot a ser excluído fica em estado; null = dialog fechado.
    const [botParaExcluir, setBotParaExcluir] = useState(null);
    const [botParaClonar, setBotParaClonar] = useState(null);
    const [nomeClonar, setNomeClonar] = useState("");
    const [clonando, setClonando] = useState(false);

    const executarExclusao = async () => {
        if (!botParaExcluir) return;
        try {
            await removerBot(botParaExcluir.id);
            toast.success("Bot removido");
            carregar();
        } catch (e) {
            toast.error("Falha ao excluir");
        }
    };

    const abrirDialogClonar = (bot) => {
        setBotParaClonar(bot);
        setNomeClonar("Cópia de " + bot.nome);
    };

    const executarClonagem = async (e) => {
        e.preventDefault();
        if (!botParaClonar || !nomeClonar.trim()) return;
        setClonando(true);
        try {
            const resultado = await clonarBot(botParaClonar.id, nomeClonar.trim());
            const regrasMsg = resultado.regras_clonadas > 0
                ? ` com ${resultado.regras_clonadas} regra(s)`
                : "";
            toast.success(`Bot clonado${regrasMsg}`);
            setBotParaClonar(null);
            setNomeClonar("");
            await carregar();
            navegar(`/bots/${resultado.bot.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha ao clonar bot");
        } finally {
            setClonando(false);
        }
    };

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
                <div className="flex items-center justify-end gap-2 mb-6">
                    <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
                        <DialogTrigger asChild>
                            <Button
                                data-testid="abrir-criar-bot"
                                className="bg-white text-zinc-950 hover:bg-zinc-200 font-medium active:scale-95 transition-all"
                            >
                                <Plus size={18} weight="bold" className="mr-2" />
                                Novo Bot
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-medium tracking-tight">Adicionar Novo Bot</DialogTitle>
                                <DialogDescription className="text-zinc-400 text-sm">
                                    Cada bot cria uma sessão de navegador isolada e persistente.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submeterCriacao} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nome" className="text-xs uppercase tracking-wide text-zinc-400">Nome do Bot</Label>
                                    <Input
                                        id="nome"
                                        data-testid="input-nome-bot"
                                        placeholder="ex: Coletor Site X"
                                        value={formCriacao.nome}
                                        onChange={(e) => setFormCriacao({ ...formCriacao, nome: e.target.value })}
                                        className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="url" className="text-xs uppercase tracking-wide text-zinc-400">URL Alvo</Label>
                                    <Input
                                        id="url"
                                        data-testid="input-url-bot"
                                        type="url"
                                        placeholder="https://exemplo.com/login"
                                        value={formCriacao.url_alvo}
                                        onChange={(e) => setFormCriacao({ ...formCriacao, url_alvo: e.target.value })}
                                        className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="intervalo" className="text-xs uppercase tracking-wide text-zinc-400">Intervalo de Auto-Refresh (ms)</Label>
                                    <Input
                                        id="intervalo"
                                        data-testid="input-intervalo-bot"
                                        type="number"
                                        min="1000"
                                        step="1000"
                                        value={formCriacao.intervalo_refresh_ms}
                                        onChange={(e) => setFormCriacao({ ...formCriacao, intervalo_refresh_ms: e.target.value })}
                                        className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                                    />
                                    <p className="text-xs text-zinc-500">Tempo entre cada reload automático após ativar.</p>
                                </div>
                                <DialogFooter className="gap-2">
                                    <Button type="button" variant="outline" data-testid="cancelar-criar-bot"
                                        className="bg-transparent border-zinc-800 hover:bg-zinc-900"
                                        onClick={() => setDialogAberto(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={criando}
                                        data-testid="confirmar-criar-bot"
                                        className="bg-white text-zinc-950 hover:bg-zinc-200">
                                        {criando ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                                        Criar bot
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <BotaoSair />
                </div>

                {carregando ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grid-skeleton">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-zinc-900 border border-zinc-800 rounded-md p-4 flex flex-col gap-3 animate-pulse"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                                        <div className="h-3 bg-zinc-800/60 rounded w-1/2"></div>
                                    </div>
                                    <div className="h-5 w-16 bg-zinc-800 rounded-full"></div>
                                </div>
                                <div className="h-3 bg-zinc-800/40 rounded w-1/3"></div>
                                <div className="flex items-center gap-2 pt-3 border-t border-zinc-800/60">
                                    <div className="h-8 bg-zinc-800 rounded flex-1"></div>
                                    <div className="h-8 w-8 bg-zinc-800 rounded"></div>
                                    <div className="h-8 w-8 bg-zinc-800 rounded"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : bots.length === 0 ? (
                    <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center" data-testid="estado-vazio">
                        <Robot size={42} className="mx-auto text-zinc-700 mb-4" />
                        <h3 className="text-lg font-medium">Nenhum bot criado ainda</h3>
                        <p className="text-sm text-zinc-500 mt-1">Clique em "Novo Bot" para começar a capturar cookies e requisições.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grid-bots">
                        {bots.map((bot) => (
                            <div
                                key={bot.id}
                                data-testid={`card-bot-${bot.id}`}
                                role="link"
                                tabIndex={0}
                                onClick={(e) => {
                                    // Ignora cliques em elementos interativos internos
                                    if (e.target.closest('button, a, [role="button"]')) return;
                                    navegar(`/bots/${bot.id}`);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        if (e.target.closest('button, a')) return;
                                        e.preventDefault();
                                        navegar(`/bots/${bot.id}`);
                                    }
                                }}
                                className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/80 rounded-md p-4 transition-all flex flex-col gap-3 cursor-pointer focus:outline-none focus:ring-1 focus:ring-zinc-600"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-zinc-100 truncate">{bot.nome}</h3>
                                        <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5 truncate">
                                            <Globe size={12} className="flex-shrink-0" />
                                            <span className="truncate">{bot.url_alvo}</span>
                                        </div>
                                    </div>
                                    <Badge
                                        data-testid={`status-${bot.id}`}
                                        variant="outline"
                                        className={`text-[10px] font-mono uppercase tracking-wider ${corStatus[bot.status] || corStatus.parado}`}
                                    >
                                        {bot.status === "rodando" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 pulso inline-block" />}
                                        {labelStatus[bot.status] || bot.status}
                                    </Badge>
                                </div>

                                {bot.ultimo_erro && (
                                    <p className="text-xs text-red-400/80 line-clamp-2 font-mono">{bot.ultimo_erro}</p>
                                )}

                                <div className="text-[10px] text-zinc-600 font-mono">
                                    refresh {Math.round(bot.intervalo_refresh_ms / 1000)}s · {bot.auto_refresh_ativo ? "auto ATIVO" : "auto OFF"}
                                </div>

                                <div className="flex items-center gap-2 pt-2 border-t border-zinc-800/60">
                                    {bot.status === "rodando" ? (
                                        <Button size="sm" variant="outline"
                                            data-testid={`parar-${bot.id}`}
                                            className="flex-1 bg-transparent border-zinc-800 hover:bg-zinc-800 text-zinc-200"
                                            onClick={() => acaoBot(bot.id, pararBot, "Bot parado")}>
                                            <Pause size={14} className="mr-1" /> Parar
                                        </Button>
                                    ) : (
                                        <Button size="sm"
                                            data-testid={`iniciar-${bot.id}`}
                                            className="flex-1 bg-white text-zinc-950 hover:bg-zinc-200"
                                            onClick={() => acaoBot(bot.id, iniciarBot, "Bot iniciado")}>
                                            <Play size={14} weight="fill" className="mr-1" /> Iniciar
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" asChild
                                        data-testid={`abrir-${bot.id}`}
                                        className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-zinc-200">
                                        <Link to={`/bots/${bot.id}`}><ArrowsClockwise size={14} /></Link>
                                    </Button>
                                    <Button size="sm" variant="outline"
                                        data-testid={`clonar-${bot.id}`}
                                        title="Clonar bot"
                                        className="bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-400"
                                        onClick={() => abrirDialogClonar(bot)}>
                                        <Copy size={14} />
                                    </Button>
                                    <Button size="sm" variant="outline"
                                        data-testid={`excluir-${bot.id}`}
                                        className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400"
                                        onClick={() => setBotParaExcluir(bot)}>
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <DialogConfirmacao
                aberto={!!botParaExcluir}
                setAberto={(v) => !v && setBotParaExcluir(null)}
                titulo="Excluir bot?"
                descricao={botParaExcluir
                    ? `O bot "${botParaExcluir.nome}" será removido junto com toda a sessão de navegador, cookies, requisições, regras e logs. Esta ação é permanente.`
                    : ""}
                rotuloAcao="Excluir definitivamente"
                onConfirmar={executarExclusao}
                testId="dialog-excluir-bot-home"
            />

            {/* Dialog de clonar bot */}
            <Dialog open={!!botParaClonar} onOpenChange={(v) => { if (!v) { setBotParaClonar(null); setNomeClonar(""); } }}>
                <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-medium tracking-tight">Clonar Bot</DialogTitle>
                        <DialogDescription className="text-zinc-400 text-sm">
                            {botParaClonar
                                ? `Criará uma cópia idêntica de "${botParaClonar.nome}" com todas as configurações e regras de encaminhamento.`
                                : ""}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={executarClonagem} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="nome-clone" className="text-xs uppercase tracking-wide text-zinc-400">Nome do Novo Bot</Label>
                            <Input
                                id="nome-clone"
                                data-testid="input-nome-clone"
                                placeholder="ex: Coletor Site X (cópia)"
                                value={nomeClonar}
                                onChange={(e) => setNomeClonar(e.target.value)}
                                autoFocus
                                className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                            />
                        </div>
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline"
                                className="bg-transparent border-zinc-800 hover:bg-zinc-900"
                                onClick={() => { setBotParaClonar(null); setNomeClonar(""); }}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={clonando || !nomeClonar.trim()}
                                data-testid="confirmar-clonar-bot"
                                className="bg-cyan-600 text-white hover:bg-cyan-700">
                                {clonando ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Copy size={16} className="mr-2" />}
                                Clonar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
